



































import Enumerable from "linq";
import InfiniteLoading from "vue-infinite-loading";
import { ElTree } from "element-ui/types/tree";
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import {
  FormatReference,
  IFormatReferenceTreeNode,
  IFormatReferenceTreeData
} from "../../../../domain/format";
import { Directory } from "../../../../domain/drawing";
import { TimeMeasure } from "../../../../utilities";
import { ModelEditableMixin } from "../../../system/mixins";
import { DirectoryTreeNodesFactory } from "../../../system/utilities/DirectoryTreeNodesFactory";
import { IFormatTreeSelectable, ICancelSelectFormatTree } from ".";
import { FormatTreeNodeUtility } from "./utilities";
import { ITreeCollapsable } from "../../../system";

@Component({
  components: {
    InfiniteLoading
  },
})
export default class FormatReferencesTreeSelector extends Mixins(ModelEditableMixin) implements IFormatTreeSelectable, ICancelSelectFormatTree, ITreeCollapsable {
  data(): any {
    return {
      infiniteLoading: null,
      chunkedCount: 0,
      loadedNodes: null,
      nodesData: [],
      selectedNode: null as IFormatReferenceTreeNode,
      reservedResetChunkList: false,
    };
  }
  infiniteLoading: InfiniteLoading;
  chunkedCount: number;
  loadedNodes: IFormatReferenceTreeData[];
  nodesData: IFormatReferenceTreeData[];
  selectedNode: IFormatReferenceTreeNode;
  reservedResetChunkList: boolean;

  @Prop({ default: false })
  isShown: boolean;

  @Prop({ default: 0 })
  loadChunkSize: number;

  @Prop()
  directories: Directory<string, FormatReference>[];

  @Prop()
  filter: (source: FormatReference | Directory<string, FormatReference>) => boolean;

  @Prop({ default: false })
  selectedDirectoryOnly: boolean;

  mounted(): void {
    this.infiniteLoading = this.$refs.infiniteLoading as InfiniteLoading;
  }

  onInfinite(): void {
    this.chunkList();
  }

  private chunkList(): void {
    for (let i = this.chunkedCount; i < (this.chunkedCount + this.loadChunkSize); i++) {
      if (i >= this.filteredNodesData.length) {
        this.infiniteLoading.stateChanger.complete();
        return;
      }
      this.loadedNodes.push(this.filteredNodesData[i]);
    }
    this.chunkedCount += this.loadChunkSize;
    this.infiniteLoading.stateChanger.loaded();
  }

  private reserveResetChunkList() {
    if (this.isShown) {
      this.resetChunkList();
    } else {
      this.reservedResetChunkList = true;
    }
  }

  private resetChunkList(): void {
    this.reservedResetChunkList = false;
    this.loadedNodes = [];
    this.chunkedCount = 0;
    if (this.infiniteLoading) {
      this.infiniteLoading.stateChanger.reset();
    }
  }

  selectDirectoryNode(key: string): boolean {
    if (!this.getTreeComponent()) {
      return false;
    }
    const found = this.findDirectoryNode(key);
    if (!found) {
      return false;
    }
    FormatReferencesTreeSelector._nodeUtility.expandNode(found);
    this.$nextTick(() => {
      this.getTreeComponent().setCurrentKey(found.key);
      this.selectedNode = found;
    });
    return true;
  }

  findDirectoryNode(key: string, node?: IFormatReferenceTreeNode): IFormatReferenceTreeNode {
    return FormatReferencesTreeSelector._nodeUtility.findNode(
      node ? node : (this.getTreeComponent() as any).root,  // rootが必要なのでanyに変換
      n => ((n.data.source instanceof Directory) && (n.data.source.key === key))
    );
  }

  @Watch("isShown")
  onIsShownUpdated(newValue: boolean, oldValue: boolean) {
    if (newValue && this.reservedResetChunkList) {
      this.resetChunkList();
    }
  }

  @Watch("filter")
  onFilterUpdated(
    newValue: (source: FormatReference | Directory<string, FormatReference>) => boolean,
    oldValue: (source: FormatReference | Directory<string, FormatReference>) => boolean
  ): void {
    this.createNodes();
    this.reserveResetChunkList();
    this.cancelSelectFormatTree();
    this.updateEditingSource();
  }

  get isLoaded(): boolean {
    return (this.nodesData !== null)
        && (this.nodesData !== undefined)
        && (0 < this.nodesData.length);
  }

  createNodes(): void {
    TimeMeasure.log(
      "TreeNodes生成",
      () => {
        const factory = new DirectoryTreeNodesFactory<string, FormatReference>();
        this.nodesData = factory.create(this.directories);
      },
      "",
      TimeMeasure.debug
    );
  }

  get filteredNodesData(): IFormatReferenceTreeData[] {
    // filterNode()内でnodesDataインスタンス内のデータを書き換えてしまっている
    // filterのWatch契機でnodesDataを読み込み直しして辻褄を合わせているが、よくないパターンに見える
    return Enumerable.from(this.nodesData)
      .where(d => this.filterNode(this.filter, d, false))
      .toArray();
  }

  private filterNode(filter: (source: FormatReference | Directory<string, FormatReference>) => boolean, data: IFormatReferenceTreeData, isAncestorDirectoryMatched: boolean): boolean {
    const isDirectoryFilterMatched = isAncestorDirectoryMatched || ((data.source instanceof Directory) && filter(data.source));
    data.children = Enumerable.from(data.children)
      .where(c => this.filterNode(filter, c, isDirectoryFilterMatched))
      .toArray();

    if (data.source instanceof Directory) {
      if (data.children.length > 0) {
        // 配下にツリー要素がある場合は表示する
        return true;
      }
      if (!data.source.entities || data.source.entities.length === 0 || !Enumerable.from(data.source.entities).any(e => filter(e))) {
        // 何も表示するべきものが無い空のフォルダは非表示
        return false;
      }
      if (isAncestorDirectoryMatched) {
        // 途中のフォルダが一致しているときは制限なく表示する
        return true;
      }
    }
    return filter(data.source);
  }

  private static readonly _nodeUtility = new FormatTreeNodeUtility();

  getTreeComponent(): ElTree<string, IFormatReferenceTreeData> {
    return this.$refs.tree as ElTree<string, IFormatReferenceTreeData>;
  }

  expandTree(): void {
    if (!this.isLoaded) {
      return;
    }
    FormatReferencesTreeSelector._nodeUtility.expandNode((this.getTreeComponent() as any).root);  // rootが必要なのでanyに変換
  }

  collapseTree(): void {
    if (!this.isLoaded) {
      return;
    }
    FormatReferencesTreeSelector._nodeUtility.collapseNode((this.getTreeComponent() as any).root);  // rootが必要なのでanyに変換
  }

  requestTreeNodeSelectStatus(): void {
    this.treeNodeSelectUpdated(this.selectedNode !== null);
  }

  cancelSelectFormatTree(): void {
    if (this.getTreeComponent()) {
      this.getTreeComponent().setCurrentKey(null);
    }
    this.selectedNode = null;
    this.treeNodeSelectUpdated(false);
  }

  updateNodeExpand(node: IFormatReferenceTreeData, nodeProperty: IFormatReferenceTreeNode, nodeSelf: any): void {
    FormatReferencesTreeSelector._nodeUtility.updateNodeExpanded(node, nodeProperty, nodeSelf);
  }

  handleNodeClicked(node: IFormatReferenceTreeData, nodeProperty: IFormatReferenceTreeNode, nodeSelf: any): void {
    this.selectedNode = nodeProperty;
  }

  created(): void {
    this.updateEditingSource();
  }

  @Watch("selectedNode")
  onSelectedNodeUpdated(newValue: IFormatReferenceTreeNode, oldValue: IFormatReferenceTreeNode) {
    this.treeNodeSelectUpdated(newValue !== null);
    this.updateEditingSource();
  }

  @Watch("directories")
  onDirectoriesUpdated(newValue: Directory<string, FormatReference>[], oldValue: Directory<string, FormatReference>[]) {
    this.createNodes();
    this.reserveResetChunkList();
    if (this.getTreeComponent()) {
      this.cancelSelectFormatTree();
    }
    this.updateEditingSource();
  }

  updateEditingSource(): void {
    // ツリー選択を選択ノードのフォーマットだけを表示するのでなくそのノード以下のフォーマットを表示するよう変更.(未選択時は全て)
    // フィルタっぽい動作なのでフィルタとして実装した方がよい気がするが、今から対応するのは当りがデカいのでフォーマットの配列を返す実装を維持.
    TimeMeasure.log(
      "選択肢更新",
      () => {
        const selectedDirectory = (this.selectedNode && this.selectedNode.data)
          ? this.selectedNode.data.source
          : null;
        const entities = this.selectedDirectoryOnly
          ? (selectedDirectory && (selectedDirectory instanceof Directory))
            ? this.extractSelectedDirectoryEntities(selectedDirectory)
            : []
          : (selectedDirectory && (selectedDirectory instanceof Directory))
            ? this.extractTreeNodesEntities([this.selectedNode.data])
            : this.extractTreeNodesEntities(this.filteredNodesData);
        this.editingSource = this.distinctEntities(entities);
      },
      "",
      TimeMeasure.debug
    );
  }

  extractSelectedDirectoryEntities(source: Directory<string, FormatReference>): FormatReference[] {
    return Enumerable.from(source.entities)
      .where(e => !this.filter || this.filter(e))
      .toArray();
  }

  private extractTreeNodesEntities(nodes: IFormatReferenceTreeData[]): FormatReference[] {
    // ツリー情報自体にはそのフォルダがフィルタに一致したものかどうかの情報は無いので、
    // 先祖に遡って判定取得している
    return Enumerable.from(nodes)
      .selectMany(node => this.extractNodeEntities(node, this.isAncestorDirectoryMatched(node)))
      .where(d => !this.filter || this.filter(d))
      .toArray();
  }

  private isAncestorDirectoryMatched(node: IFormatReferenceTreeData): boolean {
    return !!node.parent && (node.parent.source instanceof Directory) && (this.filter(node.parent.source) || this.isAncestorDirectoryMatched(node.parent));
  }

  private extractNodeEntities(node: IFormatReferenceTreeData, isAncestorDirectoryMatched: boolean): FormatReference[] {
    if (node.source instanceof FormatReference) {
      return [node.source];
    }
    const isDirectoryFilterMatched = isAncestorDirectoryMatched || ((node.source instanceof Directory) && this.filter(node.source));
    const childNodes = Enumerable.from(node.children)
      .selectMany(c => this.extractNodeEntities(c, isDirectoryFilterMatched))
      .toArray();
    return Enumerable.from(isDirectoryFilterMatched ? node.source.entities : [])
      .concat(childNodes)
      .toArray();
  }

  distinctEntities(source: FormatReference[]): FormatReference[] {
    return Enumerable.from(source)
      .groupBy(e => JSON.stringify({ id: e.id, name: e.name }),
        e => e,
        (k, g) => g.first())
      .toArray();
  }

  @Emit("treeNodeSelectUpdated")
  treeNodeSelectUpdated(value: boolean): void {}

  @Emit("nodeDoubleClicked")
  nodeDoubleClicked(): void {}

  handleNodeDoubleClicked(event: PointerEvent): void {
    if (this.selectedNode.data.source instanceof FormatReference) {
      this.nodeDoubleClicked();
    }
    if (this.selectedNode.data.source instanceof Directory) {
      // 開閉反転
      // el-treeのexpand-on-click-nodeをfalseにしないと開閉が激しい・・
      // this.selectedNode.expanded = !this.selectedNode.expanded;
    }
  }
}
